import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Api, AttachmentUpdateResponse } from 'bimplus-websdk';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';

/**
 * AttachmentsAdapterService — Adapter for the BIM+ Attachments API.
 *
 * Handles attachment (thumbnail) CRUD operations via the `api.attachments` namespace.
 * Used for updating scene thumbnails separately from scene metadata.
 *
 * Pattern follows BIM Explorer's scene-store.updateThumbnail():
 * - Scene data is updated via PUT /scenes/{id}
 * - Thumbnail is updated separately via POST /attachments/{id}
 */
@Injectable({
  providedIn: 'root',
})
export class AttachmentsAdapterService {
  private readonly api: Api = inject(BIMPLUS_API);
  private readonly http: HttpClient = inject(HttpClient);

  /**
   * Updates an attachment with new file data.
   *
   * Maps to: POST /v2/{team}/attachments/{attachmentId}
   *
   * @param attachmentId - The GUID of the attachment to update.
   * @param data - FormData containing the file data with key "data".
   * @returns Observable of the updated AttachmentUpdateResponse.
   */
  updateAttachment(attachmentId: string, data: FormData): Observable<AttachmentUpdateResponse> {
    return this.api.rxRequest(this.api.attachments.post(attachmentId, data));
  }

  /**
   * Downloads an attachment's file content from a full URL.
   *
   * Uses HttpClient with responseType: 'blob' to fetch the binary content.
   * Appends the API token as a query parameter for authentication
   * (same pattern as BIM Explorer's _getSceneThumbnailUrl).
   *
   * @param url - The full URL to download from (e.g., from scene.thumbnail)
   * @returns Observable of Blob containing the file data.
   */
  downloadAttachmentFromUrl(url: string): Observable<Blob> {
    // Append api-token query parameter for authentication when it is not already present.
    const token = this.api.getAccessToken();
    const authenticatedUrl = new URL(url);
    if (token && !authenticatedUrl.searchParams.has('api-token')) {
      authenticatedUrl.searchParams.set('api-token', token);
    }
    return this.http.get(authenticatedUrl.toString(), { responseType: 'blob' });
  }

  /**
   * Deletes an attachment.
   *
   * Maps to: DELETE /v2/{team}/attachments/{attachmentId}
   *
   * @param attachmentId - The GUID of the attachment to delete.
   * @returns Observable that completes when deletion is done.
   */
  deleteAttachment(attachmentId: string): Observable<void> {
    return this.api.rxRequest(this.api.attachments.delete(attachmentId));
  }
}
